import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { X, GripVertical, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useRef, useState, useEffect, memo } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay, DragStartEvent, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, defaultAnimateLayoutChanges } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChecklistPreviewCard } from '@/components/ChecklistPreviewCard';

interface Phase {
  id: string;
  title: string;
  isPlaceholder?: boolean;
}

interface ChecklistItem {
  id: string;
  item: string;
  comment: string;
}

interface SortablePhaseCardProps {
  phase: Phase;
  index: number;
  phaseTitles: Record<string, string>;
  onPhaseSelect: (phaseId: string, phaseTitle: string) => void;
  onRemoveTile?: (phaseId: string) => void;
  canRemove: boolean;
  isStaticReference?: boolean;
  hasMultiplePhases: boolean;
  phaseItems?: Record<string, ChecklistItem[]>;
  someoneIsDragging: boolean;
}

interface RacePhaseCarouselProps {
  phases: Phase[];
  placeholderPhase?: Phase;
  onPhaseSelect: (phaseId: string, phaseTitle: string) => void;
  onRemoveTile?: (phaseId: string) => void;
  onReorderPhases?: (phases: Phase[]) => void;
  onPlaceholderClick?: () => void;
  phaseTitles: Record<string, string>;
  isStaticReference?: boolean;
  showLoginPrompt?: boolean;
  phaseItems?: Record<string, ChecklistItem[]>;
}

// Disable layout animations during drag to prevent twitching
const animateLayoutChanges: typeof defaultAnimateLayoutChanges = (args) => {
  const { isSorting, wasDragging } = args;
  if (isSorting || wasDragging) {
    return false;
  }
  return defaultAnimateLayoutChanges(args);
};

const SortablePhaseCard = ({ phase, index, phaseTitles, onPhaseSelect, onRemoveTile, canRemove, isStaticReference, hasMultiplePhases, phaseItems, someoneIsDragging }: SortablePhaseCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: phase.id,
    animateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const items = phaseItems?.[phase.id] || [];

  // When dragging, show invisible placeholder to keep space reserved
  if (isDragging) {
    return (
      <div 
        ref={setNodeRef}
        style={style}
        className="flex-shrink-0 w-[180px] h-[210px] md:w-[280px] md:h-[320px] lg:w-[360px] lg:h-[420px] relative opacity-30 bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/30"
      />
    );
  }

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`group flex-shrink-0 w-[180px] h-[210px] md:w-[280px] md:h-[320px] lg:w-[360px] lg:h-[420px] relative ${!someoneIsDragging ? 'snap-start' : ''}`}
    >
      {/* Drag Handle - only show if there's more than one phase and not static */}
      {canRemove && !isStaticReference && hasMultiplePhases && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
               <div
                 {...attributes}
                 {...listeners}
                 className="absolute top-2 left-2 cursor-grab active:cursor-grabbing h-8 w-8 flex items-center justify-center glass-effect bg-muted/50 hover:bg-muted hover:[&>svg]:text-foreground rounded-md z-10 transition-all duration-200"
               >
                 <GripVertical className="h-4 w-4 text-primary/80 transition-colors" />
               </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="z-[100000] bg-popover text-popover-foreground border shadow-md" sideOffset={5}>
              <p>Drag to reorder</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Remove Button */}
      {canRemove && !isStaticReference && onRemoveTile && hasMultiplePhases && (
        <TooltipProvider>
          <Tooltip>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <TooltipTrigger asChild>
                     <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 h-8 w-8 p-0 glass-effect bg-destructive/10 hover:bg-destructive hover:text-destructive-foreground text-destructive z-10 rounded-md transition-all duration-200"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Remove ${phaseTitles[phase.id] || phase.title} checklist`}
                    >
                     <X className="h-4 w-4" aria-hidden="true" />
                   </Button>
                </TooltipTrigger>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove Tile</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this tile and all its checklist information. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onRemoveTile(phase.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Remove Tile
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <TooltipContent side="left" className="z-[100000] bg-popover text-popover-foreground border shadow-md" sideOffset={5}>
              <p>Remove checklist</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Preview Card */}
      <ChecklistPreviewCard
        title={phaseTitles[phase.id] || phase.title}
        items={items}
        onClick={() => onPhaseSelect(phase.id, phaseTitles[phase.id] || phase.title)}
        className="h-full"
      />
    </div>
  );
};

const RacePhaseCarouselComponent = ({ phases, placeholderPhase, onPhaseSelect, onRemoveTile, onReorderPhases, onPlaceholderClick, phaseTitles, isStaticReference = false, showLoginPrompt = false, phaseItems }: RacePhaseCarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const activePhase = activeId ? phases.find(p => p.id === activeId) : null;

  const checkScrollState = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollState();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollState);
      return () => container.removeEventListener('scroll', checkScrollState);
    }
  }, [phases]);

  const scrollLeftFn = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -280, behavior: 'smooth' });
    }
  };

  const scrollRightFn = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 280, behavior: 'smooth' });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (over && active.id !== over.id && onReorderPhases) {
      const oldIndex = phases.findIndex(p => p.id === active.id);
      const newIndex = phases.findIndex(p => p.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderPhases(arrayMove(phases, oldIndex, newIndex));
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <div className="w-full max-w-[calc(100vw-1rem)] mx-auto px-1 md:max-w-[calc(100vw-1.5rem)] md:px-2 lg:max-w-[calc(100vw-2rem)] lg:px-2 relative overflow-visible">
      {/* Left Arrow */}
      {canScrollLeft && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute -left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-8 bg-card/80 hover:bg-card/90 border border-border shadow-glass transition-all duration-200 hover:scale-105 rounded-md"
          onClick={scrollLeftFn}
          aria-label="Scroll left to view previous checklists"
        >
          <ChevronLeft className="h-4 w-4 text-destructive" aria-hidden="true" />
        </Button>
      )}
      
      {/* Right Arrow */}
      {canScrollRight && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute -right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-8 bg-card/80 hover:bg-card/90 border border-border shadow-glass transition-all duration-200 hover:scale-105 rounded-md"
          onClick={scrollRightFn}
          aria-label="Scroll right to view more checklists"
        >
          <ChevronRight className="h-4 w-4 text-destructive" aria-hidden="true" />
        </Button>
      )}
      
      {/* DndContext with closestCenter collision detection - simple like item drag */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div 
          ref={scrollContainerRef}
          className={`overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide ${!activeId ? 'snap-x snap-proximity' : ''}`}
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            touchAction: activeId ? 'none' : undefined
          }}
          role="region"
          aria-label="Checklists carousel"
          tabIndex={0}
        >
          <SortableContext items={phases.map(phase => phase.id)} strategy={horizontalListSortingStrategy}>
            <div className="flex gap-4 min-w-max pl-0 pr-8 pt-4">
              {phases.map((phase, index) => (
                 <SortablePhaseCard
                   key={phase.id}
                   phase={phase}
                   index={index}
                   phaseTitles={phaseTitles}
                   onPhaseSelect={onPhaseSelect}
                   onRemoveTile={onRemoveTile}
                   canRemove={phases.length > 1}
                   isStaticReference={isStaticReference}
                   hasMultiplePhases={phases.length > 1}
                   phaseItems={phaseItems}
                   someoneIsDragging={!!activeId}
                 />
              ))}
               {placeholderPhase && phases.length < 15 && !isStaticReference && onPlaceholderClick && (
                  <Card
                    className={`group transition-all duration-200 hover:shadow-sm bg-card border-2 border-dashed border-border hover:border-accent flex-shrink-0 w-[180px] h-[210px] md:w-[280px] md:h-[320px] lg:w-[360px] lg:h-[420px] cursor-pointer ${!activeId ? 'snap-start' : ''}`}
                   onClick={onPlaceholderClick}
                   role="button"
                   tabIndex={0}
                   aria-label="Add new checklist to this section"
                   onKeyDown={(e) => {
                     if (e.key === 'Enter' || e.key === ' ') {
                       e.preventDefault();
                       onPlaceholderClick();
                     }
                   }}
                 >
                  <CardContent className="p-4 h-full flex flex-col items-center justify-center text-center">
                    <Plus className="w-5 h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-accent mb-2 md:mb-3 lg:mb-4 transition-transform group-hover:scale-110" />
                    <div className="text-muted-foreground font-normal text-sm md:text-lg lg:text-xl transition-colors duration-300 group-hover:text-accent">
                      Add Checklist ({phases.length}/15)
                    </div>
                    <div className="text-muted-foreground text-xs md:text-sm lg:text-base font-light mt-1 md:mt-2">
                      Click to create a new checklist
                    </div>
                  </CardContent>
                  </Card>
                )}
            </div>
          </SortableContext>
        </div>

        {/* Simple DragOverlay */}
        <DragOverlay dropAnimation={null}>
          {activePhase ? (
            <div className="flex-shrink-0 w-[180px] h-[210px] md:w-[280px] md:h-[320px] lg:w-[360px] lg:h-[420px] shadow-2xl opacity-90">
              <ChecklistPreviewCard
                title={phaseTitles[activePhase.id] || activePhase.title}
                items={phaseItems?.[activePhase.id] || []}
                onClick={() => {}}
                className="h-full"
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export const RacePhaseCarousel = memo(RacePhaseCarouselComponent);
